import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import UpdatePaymentMethodModalWrapper from '../UpdatePaymentMethodModalWrapper';

const PaymentRequired = ({ user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  const handleGoToSubscription = () => {
    navigate('/subscription');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handlePaymentSuccess = () => {
    // Recharger la page pour rafraîchir le statut du compte
    window.location.reload();
  };

  // Déterminer le type de problème et adapter les messages
  const isSuspended = user?.account_status === 'suspended';
  const isGracePeriod = user?.account_status === 'grace_period';
  const isIncompleteSubscription = user?.payment_required === true;
  const isPaymentFailure = !isSuspended && !isGracePeriod && !isIncompleteSubscription && (user?.failed_payments > 0);

  const getTitle = () => {
    if (isSuspended) return t('billing.paymentRequired.titles.suspended');
    if (isGracePeriod) return t('billing.paymentRequired.titles.gracePeriod');
    if (isPaymentFailure) return t('billing.paymentRequired.titles.paymentFailure');
    return t('billing.paymentRequired.titles.incomplete');
  };

  const getMessage = () => {
    if (isSuspended) {
      const reasonPart = user.suspension_reason 
        ? t('billing.paymentRequired.messages.suspendedReason', { reason: user.suspension_reason.toLowerCase() })
        : t('billing.paymentRequired.messages.suspendedDefault');
      return t('billing.paymentRequired.messages.suspended', { reason: reasonPart });
    }
    if (isGracePeriod) {
      return t('billing.paymentRequired.messages.gracePeriod');
    }
    if (isPaymentFailure) {
      return t('billing.paymentRequired.messages.paymentFailure', { count: user.failed_payments });
    }
    return t('billing.paymentRequired.messages.incomplete');
  };

  const getButtonText = () => {
    if (isSuspended || isGracePeriod) return t('billing.paymentRequired.buttons.resolve');
    if (isPaymentFailure) return t('billing.paymentRequired.buttons.updateCard');
    return t('billing.paymentRequired.buttons.finalize');
  };

  const getIconColor = () => {
    if (isSuspended) return 'bg-red-100 text-red-600';
    if (isGracePeriod) return 'bg-yellow-100 text-yellow-600';
    if (isPaymentFailure) return 'bg-orange-100 text-orange-600';
    return 'bg-yellow-100 text-yellow-600';
  };

  const getButtonColor = () => {
    if (isSuspended) return 'bg-red-600 hover:bg-red-700';
    if (isGracePeriod) return 'bg-yellow-600 hover:bg-yellow-700';
    if (isPaymentFailure) return 'bg-orange-600 hover:bg-orange-700';
    return 'bg-blue-600 hover:bg-blue-700';
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
        {/* Icône d'alerte */}
        <div className="flex justify-center mb-6">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${getIconColor()}`}>
            <ExclamationTriangleIcon className="w-8 h-8" />
          </div>
        </div>

        {/* Titre */}
        <h1 className="text-2xl font-bold text-gray-900 text-center mb-4">
          {getTitle()}
        </h1>

        {/* Compteur d'échecs */}
        {isPaymentFailure && (
          <div className="flex justify-center mb-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
              {user.failed_payments} échec{user.failed_payments > 1 ? 's' : ''} de paiement détecté{user.failed_payments > 1 ? 's' : ''}
            </span>
          </div>
        )}

        {/* Message */}
        <div className="text-center mb-6">
          <p className="text-gray-600 mb-3">
            {t('billing.paymentRequired.greeting', { name: user?.firstName })}
          </p>
          <p className="text-gray-600">
            {getMessage()}
          </p>
        </div>

        {/* Informations sur l'abonnement actuel */}
        {user?.subscription && (
          <div className={`p-4 rounded-lg mb-6 ${isSuspended ? 'bg-red-50' : isGracePeriod ? 'bg-yellow-50' : isPaymentFailure ? 'bg-orange-50' : 'bg-blue-50'}`}>
            <h3 className={`font-semibold mb-2 ${isSuspended ? 'text-red-900' : isGracePeriod ? 'text-yellow-900' : isPaymentFailure ? 'text-orange-900' : 'text-blue-900'}`}>
              {t('billing.paymentRequired.subscription.label', {
                status: isSuspended ? t('billing.paymentRequired.subscription.suspended') : 
                       isGracePeriod ? t('billing.paymentRequired.subscription.current') : 
                       t('billing.paymentRequired.subscription.current')
              })}
            </h3>
            <p className={`${isSuspended ? 'text-red-700' : isGracePeriod ? 'text-yellow-700' : isPaymentFailure ? 'text-orange-700' : 'text-blue-700'}`}>
              {user.subscription.title}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={isPaymentFailure ? () => setShowUpdateModal(true) : handleGoToSubscription}
            className={`w-full text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 ${getButtonColor()}`}
          >
            {getButtonText()}
          </button>
          
          <button
            onClick={handleLogout}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-lg transition-colors duration-200"
          >
            {t('billing.paymentRequired.buttons.logout')}
          </button>
        </div>

        {/* Information supplémentaire */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-2">
            {isSuspended ? t('billing.paymentRequired.infoSections.suspended.title') : 
             isGracePeriod ? t('billing.paymentRequired.infoSections.gracePeriod.title') : 
             isPaymentFailure ? t('billing.paymentRequired.infoSections.paymentFailure.title') :
             t('billing.paymentRequired.infoSections.incomplete.title')}
          </h4>
          <ul className="text-sm text-gray-600 space-y-1">
            {isSuspended ? 
              t('billing.paymentRequired.infoSections.suspended.items', { returnObjects: true }).map((item, index) => (
                <li key={index}>• {item}</li>
              )) :
             isGracePeriod ? 
              t('billing.paymentRequired.infoSections.gracePeriod.items', { returnObjects: true }).map((item, index) => (
                <li key={index}>• {item}</li>
              )) :
             isPaymentFailure ?
              t('billing.paymentRequired.infoSections.paymentFailure.items', { returnObjects: true }).map((item, index) => (
                <li key={index}>• {item}</li>
              )) :
              t('billing.paymentRequired.infoSections.incomplete.items', { returnObjects: true }).map((item, index) => (
                <li key={index}>• {item}</li>
              ))
            }
          </ul>
        </div>
      </div>

      {/* Modal de mise à jour du moyen de paiement */}
      <UpdatePaymentMethodModalWrapper
        isOpen={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        onSuccess={handlePaymentSuccess}
        hasPendingPayment={isPaymentFailure || isGracePeriod}
      />
    </div>
  );
};

export default PaymentRequired;